/**
 * ClientsContext - Gestión específica del estado de clientes
 * Separado del DataContext para mejor organización y performance
 *
 * Conectado con la API real de clientes
 *
 * IMPORTANT: This context now uses TenantContext to filter clients
 * by the selected project and company.
 *
 * Supports server-side pagination, filtering, and global stats.
 */

import React, { createContext, useContext, useState, ReactNode, useCallback, useMemo, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Client } from '@/modules/clients/types';
import { apiClient } from '@/lib/api-client';
import { useAuth } from './AuthContext';
import { useTenant } from './TenantContext';

interface ClientsResponse {
  count: number;
  results: Client[];
}

export interface ClientsFilters {
  phase?: string;
  credit_status?: string;
  payment_status?: string;
  ownership_status?: string;
  search?: string;
}

/** Stats shape returned by /admin/clients/stats/ */
export interface ClientStatsEntry {
  count: number;
  total_value: number;
}

export interface ClientsStats {
  total: ClientStatsEntry;
  estado: Record<string, ClientStatsEntry>;
  funnel: Record<string, ClientStatsEntry>;
  pagos: Record<string, ClientStatsEntry>;
  credito: Record<string, ClientStatsEntry>;
}

interface ClientsContextType {
  // Estado
  clients: Client[];
  clientCount: number;
  isLoading: boolean;

  // Pagination
  currentPage: number;
  pageSize: number;
  totalCount: number;
  setCurrentPage: (page: number) => void;
  setPageSize: (size: number) => void;

  // Filters
  filters: ClientsFilters;
  setFilters: (filters: ClientsFilters) => void;

  // Global stats (from backend, not page-dependent)
  stats: ClientsStats | null;
  isLoadingStats: boolean;

  // Cancelled clients count (for badge)
  cancelledCount: number;
  isLoadingCancelled: boolean;

  // Acciones
  setClients: React.Dispatch<React.SetStateAction<Client[]>>;
  updateClient: (id: string, updates: Partial<Client>) => void;
  deleteClient: (id: string) => void;
  addClient: (client: Client) => void;
  refreshClients: () => void;
}

const ClientsContext = createContext<ClientsContextType | undefined>(undefined);

export function ClientsProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { selectedProject, effectiveCompanyId, isPlatform } = useTenant();
  const queryClient = useQueryClient();
  const [localClients, setLocalClients] = useState<Client[]>([]);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  // Filters state
  const [filters, setFiltersState] = useState<ClientsFilters>({});

  // Reset page to 1 when filters, project, or company change
  const setFilters = useCallback((newFilters: ClientsFilters) => {
    setFiltersState(newFilters);
    setCurrentPage(1);
  }, []);

  // Reset pagination when project/company changes
  useEffect(() => {
    setCurrentPage(1);
    setFiltersState({});
  }, [selectedProject?.id, effectiveCompanyId]);

  // Build base query params for filtering by project/company
  const baseParams = useMemo(() => {
    const params: Record<string, string> = {};
    if (selectedProject?.id) {
      params.project_id = selectedProject.id;
    }
    if (!selectedProject?.id && effectiveCompanyId) {
      params.company_id = effectiveCompanyId;
    }
    return params;
  }, [selectedProject?.id, effectiveCompanyId]);

  // Build query string with pagination and filters
  const queryString = useMemo(() => {
    const params: Record<string, string> = {
      ...baseParams,
      ownership_status: filters.ownership_status || 'all',
      page: String(currentPage),
      page_size: String(pageSize),
    };
    if (filters.phase) params.phase = filters.phase;
    if (filters.credit_status) params.credit_status = filters.credit_status;
    if (filters.payment_status) params.payment_status = filters.payment_status;
    if (filters.search) params.search = filters.search;

    const searchParams = new URLSearchParams(params);
    return `?${searchParams.toString()}`;
  }, [baseParams, currentPage, pageSize, filters]);

  const isEnabled = isAuthenticated && !authLoading && (!!selectedProject?.id || !!effectiveCompanyId || isPlatform);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['clients', 'list', selectedProject?.id, effectiveCompanyId, currentPage, pageSize, filters],
    queryFn: async (): Promise<ClientsResponse> => {
      try {
        return await apiClient.get<ClientsResponse>(`/admin/clients/${queryString}`);
      } catch (error) {
        console.error('[ClientsContext] Error fetching clients:', error);
        return { count: 0, results: [] };
      }
    },
    staleTime: 1 * 60 * 1000,
    placeholderData: (prev) => prev, // Keep previous data while loading new page/filters
    retry: false,
    enabled: isEnabled,
  });

  // ── GLOBAL STATS (independent of pagination/filters) ──
  const statsQueryString = useMemo(() => {
    const searchParams = new URLSearchParams(baseParams);
    return `?${searchParams.toString()}`;
  }, [baseParams]);

  const { data: statsData, isLoading: isLoadingStats } = useQuery({
    queryKey: ['clients', 'stats', selectedProject?.id, effectiveCompanyId],
    queryFn: async (): Promise<ClientsStats> => {
      try {
        return await apiClient.get<ClientsStats>(`/admin/clients/stats/${statsQueryString}`);
      } catch (error) {
        console.error('[ClientsContext] Error fetching client stats:', error);
        return {
          total: { count: 0, total_value: 0 },
          estado: {},
          funnel: {},
          pagos: {},
          credito: {},
        };
      }
    },
    staleTime: 2 * 60 * 1000,
    placeholderData: (prev) => prev,
    retry: false,
    enabled: isEnabled,
  });

  // Fetch cancelled count for badge in clients page
  const cancelledQueryString = useMemo(() => {
    const searchParams = new URLSearchParams({ ...baseParams, ownership_status: 'cancelled' });
    return `?${searchParams.toString()}`;
  }, [baseParams]);

  const { data: cancelledData, isLoading: isLoadingCancelled } = useQuery({
    queryKey: ['clients', 'cancelled-count', selectedProject?.id, effectiveCompanyId],
    queryFn: async (): Promise<ClientsResponse> => {
      try {
        return await apiClient.get<ClientsResponse>(`/admin/clients/${cancelledQueryString}`);
      } catch {
        return { count: 0, results: [] };
      }
    },
    staleTime: 5 * 60 * 1000,
    retry: false,
    enabled: isEnabled,
  });

  const clients = data?.results || localClients;
  const clientCount = data?.count || clients.length;
  const totalCount = data?.count || 0;
  const cancelledCount = cancelledData?.count || cancelledData?.results?.length || 0;

  // Actualizar un cliente
  const updateClient = useCallback((id: string, updates: Partial<Client>) => {
    setLocalClients(prevClients =>
      prevClients.map(client =>
        client.id === id ? { ...client, ...updates } : client
      )
    );
    queryClient.invalidateQueries({ queryKey: ['clients'] });
  }, [queryClient]);

  // Eliminar un cliente
  const deleteClient = useCallback((id: string) => {
    setLocalClients(prevClients => prevClients.filter(client => client.id !== id));
    queryClient.invalidateQueries({ queryKey: ['clients'] });
  }, [queryClient]);

  // Agregar un cliente
  const addClient = useCallback((client: Client) => {
    setLocalClients(prevClients => [...prevClients, client]);
    queryClient.invalidateQueries({ queryKey: ['clients'] });
  }, [queryClient]);

  // Refrescar clientes
  const refreshClients = useCallback(() => {
    refetch();
  }, [refetch]);

  // Handle page size change — reset to page 1
  const handleSetPageSize = useCallback((newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(1);
  }, []);

  const value: ClientsContextType = {
    clients,
    clientCount,
    isLoading,
    currentPage,
    pageSize,
    totalCount,
    setCurrentPage,
    setPageSize: handleSetPageSize,
    filters,
    setFilters,
    stats: statsData || null,
    isLoadingStats,
    cancelledCount,
    isLoadingCancelled,
    setClients: setLocalClients,
    updateClient,
    deleteClient,
    addClient,
    refreshClients,
  };

  return <ClientsContext.Provider value={value}>{children}</ClientsContext.Provider>;
}

export function useClientsContext() {
  const context = useContext(ClientsContext);
  if (context === undefined) {
    throw new Error('useClientsContext must be used within a ClientsProvider');
  }
  return context;
}
